export interface ArticleSection {
  type: 'paragraph' | 'heading' | 'list';
  text?: string;
  items?: string[];
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  time: string;
  readTime: string;
  author: {
    name: string;
    role: string;
    avatar: string;
  };
  image: string;
  sourceName: string;
  sourceUrl: string;
  topics: string[];
  sections: ArticleSection[];
}


export const AI_SKILLED_BLOG_SOURCE = 'AI Skilled Blog' as const;

export const ARTICLES: Article[] = [
  {
    id: 'sif-2026',
    slug: 'skilled-at-sif-2026',
    title: 'Skilled at SIF 2026: building the future of go-to-market',
    excerpt:
      'Mohammad AlNajjar showcased Skilled at the Start Smart Investment Forum in Riyadh, connecting with investors and GTM leaders around agentic AI for B2B sales.',
    category: 'Company News',
    date: 'April 28, 2026',
    time: '10:00',
    readTime: '4 min read',
    author: {
      name: 'Mohammad AlNajjar',
      role: 'CEO & Founder',
      avatar: '',
    },
    image: 'assets/company/events/sif/sif-showcase-promo.png',
    sourceName: 'AI Skilled Blog',
    sourceUrl: 'https://aiskilled.co/content-hub/skilled-at-sif-2026',
    topics: [
      'Start Smart Investment Forum 2026',
      'Showcasing Skilled and Ali',
      'Partnerships on the signing platform',
      'See photos from the event',
    ],
    sections: [
      {
        type: 'paragraph',
        text:
          'Skilled joined the Start Smart Investment Forum (SIF) 2026 at The Garage in Riyadh. Founder and CEO Mohammad AlNajjar showcased how Skilled is building the future of go-to-market with Ali, our agentic AI digital employee for B2B sales teams.',
      },
      {
        type: 'heading',
        text: 'Start Smart Investment Forum 2026',
      },
      {
        type: 'paragraph',
        text:
          'SIF brings together founders, investors, and ecosystem partners under the theme “Where Ambition Meets Investment.” For Skilled, it was a chance to meet the people shaping Saudi Arabia’s innovation economy and share a practical vision for agentic AI in outbound sales.',
      },
      {
        type: 'heading',
        text: 'Showcasing Skilled and Ali',
      },
      {
        type: 'list',
        items: [
          'How Ali runs prospecting, personalized outreach, and meeting booking.',
          'Why B2B teams need agentic workflows they can verify in their CRM.',
          'Skilled’s focus on practical AI, trust by design, and measurable outcomes.',
        ],
      },
      {
        type: 'heading',
        text: 'Partnerships on the signing platform',
      },
      {
        type: 'paragraph',
        text:
          'Skilled participated in the forum’s signing platform alongside partners including The Garage, Falak Investment Hub, KACST, and Community Jameel. These conversations strengthen the ecosystem we build for: founders and sales teams who want growth without adding headcount for repetitive work.',
      },
      {
        type: 'heading',
        text: 'See photos from the event',
      },
      {
        type: 'paragraph',
        text:
          'Browse our SIF 2026 photo gallery on the Company page under Events for showcase and signing moments from Riyadh. We will keep adding events as Skilled shows up in more forums across the region.',
      },
    ],
  },
  {
    id: '1',
    slug: 'top-ai-cold-email-personalization-tools',
    title: 'Top AI Cold Email Personalization Tools',
    excerpt:
      'A practical breakdown of AI writing tools, sales engagement platforms, and CRM AI features to help teams choose the right stack.',
    category: 'AI Sales',
    date: 'July 12, 2025',
    time: '12:05',
    readTime: '8 min read',
    author: {
      name: 'Skilled',
      role: 'Editorial',
      avatar: '',
    },
    image: 'assets/content-hub/top-ai-cold-email-personalization-tools.webp',
    sourceName: 'AI Skilled Blog',
    sourceUrl: 'https://aiskilled.co/blog/top-ai-cold-email-personalization-tools/',
    topics: [
      'AI writing assistants for personalization',
      'Sales engagement platforms with built-in AI',
      'CRM AI capabilities for outbound teams',
      'How to choose based on stack and budget',
      'Operational checklist before rollout',
    ],
    sections: [
      {
        type: 'paragraph',
        text:
          'Legacy Skilled research showed that AI cold email success comes less from one magic tool and more from choosing the right category for your current sales maturity. Teams usually need either better writing quality, stronger sequence orchestration, or deeper CRM intelligence.',
      },
      {
        type: 'heading',
        text: 'AI writing assistants: best for message quality',
      },
      {
        type: 'paragraph',
        text:
          'Writing-focused tools help teams generate stronger subject lines, first lines, and calls to action quickly. They are ideal for small teams that already have a sequencing system but need faster personalization at scale.',
      },
      {
        type: 'heading',
        text: 'Sales engagement platforms with AI: best for execution',
      },
      {
        type: 'paragraph',
        text:
          'SEPs combine workflow automation, reply tracking, and performance analytics. Their AI layer usually improves send-time optimization, lead prioritization, and sequence recommendations, making them a strong fit for structured outbound operations.',
      },
      {
        type: 'heading',
        text: 'CRM AI capabilities: best for system-wide context',
      },
      {
        type: 'paragraph',
        text:
          'If your team already lives in a CRM, native AI features can improve qualification, forecasting, and data automation without adding too many moving parts. This approach works well when governance and cross-team visibility matter most.',
      },
      {
        type: 'heading',
        text: 'How to choose the right setup',
      },
      {
        type: 'list',
        items: [
          'Start with your bottleneck: copy quality, speed, or CRM visibility.',
          'Prioritize integrations with your existing email and CRM stack.',
          'Evaluate time-to-value, not just feature count.',
          'Run a 2-4 week pilot with clear success metrics.',
        ],
      },
      {
        type: 'heading',
        text: 'Where Ali fits in this landscape',
      },
      {
        type: 'paragraph',
        text:
          'Ali is designed as an autonomous AI SDR layer that combines personalization, follow-up orchestration, and booking workflows in one operating motion. That helps teams avoid patching too many disconnected tools as outbound volume grows.',
      },
    ],
  },
  {
    id: '2',
    slug: 'sales-funnel-leakage-5-fixes-for-a-stronger-pipeline',
    title: 'Sales Funnel Leakage: 5 Fixes for a Stronger Pipeline',
    excerpt:
      'Diagnose where pipeline momentum drops and apply five concrete fixes across targeting, speed-to-lead, nurture, handoff, and closing.',
    category: 'Pipeline',
    date: 'July 10, 2025',
    time: '15:40',
    readTime: '9 min read',
    author: {
      name: 'Skilled',
      role: 'Editorial',
      avatar: '',
    },
    image: 'assets/content-hub/sales-funnel-leakage.webp',
    sourceName: 'AI Skilled Blog',
    sourceUrl: 'https://aiskilled.co/blog/5-common-leaks-and-how-to-plug-them/',
    topics: [
      'Leak 1: top-of-funnel mismatch',
      'Leak 2: slow response time',
      'Leak 3: mid-funnel nurture gap',
      'Leak 4: marketing-sales handoff friction',
      'Leak 5: bottom-funnel conversion blockers',
    ],
    sections: [
      {
        type: 'paragraph',
        text:
          'One of Skilled’s older resources highlighted a recurring truth: most teams do not have a lead problem, they have a leakage problem. Revenue misses usually come from silent drop-offs between funnel stages, not from a complete lack of demand.',
      },
      {
        type: 'heading',
        text: 'Leak #1: top-of-funnel mismatch',
      },
      {
        type: 'paragraph',
        text:
          'Broad ICP definitions attract traffic that will never buy. Tightening role, industry, and urgency criteria early improves reply quality and reduces wasted SDR bandwidth.',
      },
      {
        type: 'heading',
        text: 'Leak #2: speed-to-lead failure',
      },
      {
        type: 'paragraph',
        text:
          'When first response slips from minutes to hours, conversion drops sharply. Automated first-touch plus instant routing to the right owner protects lead intent before it fades.',
      },
      {
        type: 'heading',
        text: 'Leak #3: mid-funnel black hole',
      },
      {
        type: 'paragraph',
        text:
          'Leads that are not ready now often disappear without structured nurturing. Segment by stage and persona, then run contextual follow-ups across email and social until intent resurfaces.',
      },
      {
        type: 'heading',
        text: 'Leak #4: broken marketing-sales handoff',
      },
      {
        type: 'list',
        items: [
          'Agree on one SQL definition used by both teams.',
          'Add SLA targets for acceptance and first-touch timing.',
          'Automate assignment and ownership tracking in CRM.',
        ],
      },
      {
        type: 'heading',
        text: 'Leak #5: closing-stage friction',
      },
      {
        type: 'paragraph',
        text:
          'Complex booking and quote flows cause avoidable no-shows and stalled deals. Reduce steps, automate reminders, and keep approvals lightweight so momentum is not lost after demo intent is already high.',
      },
      {
        type: 'heading',
        text: 'Build a watertight pipeline with automation',
      },
      {
        type: 'paragraph',
        text:
          'The fastest route to growth is often fixing leakage before increasing spend. Ali helps teams respond faster, nurture consistently, and maintain cleaner handoffs so more existing demand becomes booked revenue.',
      },
    ],
  },
  {
    id: '3',
    slug: '5-industries-where-ai-sdrs-dominate-cold-outreach',
    title: 'AI SDR Industries: 5 Sectors Where Automation Wins Big',
    excerpt:
      'Why SaaS, FinTech, real estate, healthcare tech, and staffing teams often see outsized gains from AI-powered outbound automation.',
    category: 'AI Sales',
    date: 'July 8, 2025',
    time: '10:20',
    readTime: '8 min read',
    author: {
      name: 'Skilled',
      role: 'Editorial',
      avatar: '',
    },
    image: 'assets/content-hub/ai-sdr-industries.png',
    sourceName: 'AI Skilled Blog',
    sourceUrl: 'https://aiskilled.co/blog/5-industries-where-ai-sdrs-dominate-cold-outreach/',
    topics: [
      'SaaS and SMB outbound acceleration',
      'Commercial real estate timing-based outreach',
      'FinTech compliance-aware messaging',
      'Healthcare tech segmentation and privacy constraints',
      'Staffing and recruiting high-volume workflows',
    ],
    sections: [
      {
        type: 'paragraph',
        text:
          'Another legacy Skilled resource emphasized that AI SDR performance varies by industry. The strongest results tend to appear where targeting is structured, lead volume is high, and response speed matters more than long relationship cycles.',
      },
      {
        type: 'heading',
        text: '1) SaaS: high signal velocity and short cycles',
      },
      {
        type: 'paragraph',
        text:
          'SaaS teams typically track rich usage and firmographic signals, making personalization easier to automate. AI SDRs can process large lead pools and run adaptive follow-ups while human reps focus on demos and qualification calls.',
      },
      {
        type: 'heading',
        text: '2) Commercial real estate: timing creates advantage',
      },
      {
        type: 'paragraph',
        text:
          'Outreach success often depends on timing events such as lease cycles and listing changes. AI automation helps monitor these triggers continuously and launch highly localized campaigns at the right moment.',
      },
      {
        type: 'heading',
        text: '3) FinTech and payments: precision matters',
      },
      {
        type: 'paragraph',
        text:
          'FinTech outbound requires clear segmentation and careful language. AI-assisted workflows help teams keep messaging aligned by vertical, transaction profile, and product fit while maintaining faster cadence.',
      },
      {
        type: 'heading',
        text: '4) Healthcare tech: complex targeting at scale',
      },
      {
        type: 'list',
        items: [
          'Segment by specialty, facility size, and operating model.',
          'Use pre-approved templates for sensitive communication contexts.',
          'Automate reminders and nurture steps without over-contacting.',
        ],
      },
      {
        type: 'heading',
        text: '5) Staffing and recruiting: speed plus volume',
      },
      {
        type: 'paragraph',
        text:
          'Recruiting teams handle fast-moving opportunities where delayed outreach kills conversion. AI SDR motions support high-throughput prospecting and consistent follow-up while recruiters prioritize conversations and placement quality.',
      },
      {
        type: 'heading',
        text: 'What these industries share',
      },
      {
        type: 'paragraph',
        text:
          'The common pattern is simple: abundant lead data, repeatable targeting criteria, and urgency in first response. These are exactly the conditions where Ali can amplify outbound capacity without sacrificing relevance.',
      },
    ],
  },
];

export function getArticleBySlug(slug: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}
